import React, { useState } from 'react';
import { GenericTable } from '@shared/components/GenericTable';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@shared/ui/card';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@shared/ui/dialog';
import { CheckCircle2, History, XCircle, CalendarIcon, Wallet, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { usePaymentAgreementDetail } from '../../hook';
import { useCurrency } from '@shared/selectors/globalSelectors';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';

const getScheduleColumns = (t: any): any[] => [
  { header: t("#"), key: 'installmentNo' as const },
  { header: t("Due Date"), key: 'dueDate' as const, render: (val: any) => val || 'N/A' },
  { header: t("Amount"), key: 'paidAmount' as const, render: (val: string) => <span className="font-semibold">${Number(val || 0).toFixed(2)}</span> },
  { 
    header: t("Status"), 
    key: 'statusDisplay' as const,
    type: 'badge',
    badgeConfig: {
      getVariant: (value: string) => {
        if (value === 'Paid') return 'success';
        if (value === 'Unpaid') return 'warning';
        return 'default';
      },
      getClassName: (value: string) => {
        if (value === 'Unpaid') return 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-50 font-normal';
        return '';
      }
    }
  },
  { header: t("Paid Date"), key: 'paidDate' as const, render: (val: any) => val || 'NA' },
];

const AgreementDetailsCard = ({ agreement }: { agreement: any }) => {
  const { t } = useTranslation();
  const isComplete = agreement.statusDisplay === 'Completed';
  const navigate = useNavigate();
  const currency = useCurrency()

  const paidInstallments = (agreement.installments || []).filter((i: any) => i.statusDisplay?.toLowerCase() === 'paid');
  const paidCount = paidInstallments.length;
  const totalCount = agreement.noOfInstallments || 0;
  
  const totalPaid = paidInstallments.reduce((sum: number, i: any) => sum + Number(i.paidAmount || 0), 0);
  const total = agreement.totalAgreementAmount ?? 'NA';
  const remaining = agreement?.remainingAmount

  return (
    <Card className="shadow-sm border-gray-200 overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between border-b pb-4 bg-white">
        <div className="flex items-center space-x-2">
          {agreement.isActive ? (
            <CheckCircle2 className="text-green-600 w-5 h-5" />
          ) : (
            <History className="text-gray-400 w-5 h-5" />
          )}
          <CardTitle className="text-lg">
            {agreement.isActive ? t("Active Payment Agreement") : t("Agreement Details")}
          </CardTitle>
        </div>
        <Badge variant="secondary" className="bg-blue-50 text-blue-600 hover:bg-blue-50 border-transparent font-medium">
          {agreement.isActive ? (agreement.installmentFrequencyDisplay ? `${agreement.installmentFrequencyDisplay} ${t("Installment")}` : t("Monthly Installments")) : agreement.statusDisplay}
        </Badge>
      </CardHeader>
      
      <CardContent className="pt-6 space-y-8 bg-white">
        {/* Info Row */}
        <div className="flex items-center space-x-6 text-xs text-muted-foreground font-medium">
          <div>{t("Agreement Id")} <span className="text-foreground font-bold ml-1">{agreement.agreementNo}</span></div>
          <div>{t("Start Date")} <span className="text-foreground font-bold ml-1">{agreement.startDate || agreement.createdDate || 'N/A'}</span></div>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 pb-6 border-b">
          <div>
            <div className="text-xs text-muted-foreground mb-1 font-medium">{t("Total Amount")}</div>
            <div className="text-2xl font-bold text-gray-900">{currency} {total}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1 font-medium">{t("Down Payment")}</div>
            <div className="text-2xl font-bold text-green-600">{currency} {agreement.downPayment}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1 font-medium">{t("Installment")}</div>
            <div className="text-2xl font-bold text-gray-900">{currency} {agreement.monthlyPayment}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1 font-medium">{t("Remaining")}</div>
            <div className="text-2xl font-bold text-orange-500">{currency} {remaining ?? 'NA'}</div>
          </div>
        </div>

        {/* Progress Row */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs font-medium">
            <span className="text-blue-600">{t("Progress")}</span>
            <span className="text-muted-foreground">{paidCount} / {totalCount} {t("Paid")}</span>
          </div>
          <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
            <div className="bg-blue-600 h-full" style={{ width: totalCount ? `${(paidCount / totalCount) * 100}%` : '0%' }}></div>
          </div>
        </div>

        {/* Table Header & Table */}
        <div className="pt-2">
          <div className="flex items-center space-x-2 mb-4 font-semibold text-gray-700">
            <CalendarIcon className="w-4 h-4 text-gray-400" />
            <span>{t("Installment Schedule")}</span>
          </div>
          <div className="rounded-md border-gray-100 overflow-hidden">
            <GenericTable 
              columns={React.useMemo(() => getScheduleColumns(t), [t])} 
              data={agreement.installments || []}
              actions={agreement.isActive ? [
                {
                  label: <div className="flex items-center gap-1.5 text-gray-700 font-medium"><Wallet className="w-3.5 h-3.5 text-gray-400" /> {t("Pay")}</div>,
                  onClick: (row: any) => {
                    sessionStorage.setItem('installmentData', JSON.stringify(row));
                    navigate('/dashboard/billing/pay?type=installment');
                  },
                  variant: "outline"
                }
              ] : []}
              actionsType="buttons"
            />
          </div>
        </div>
      </CardContent>
      {agreement.isActive && (
        <CardFooter className="justify-end border-t py-4 bg-white">
          <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-sm font-medium h-9">
            <XCircle className="w-4 h-4 mr-2" /> {t("Cancel Agreement")}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export const Installments = () => {
  const { t } = useTranslation();
  const [selectedHistoryAgreement, setSelectedHistoryAgreement] = useState<any>(null);

  const { consumerId } = getLoginDataFromStorage();

  const { data: agreementData } = usePaymentAgreementDetail({
    remote_utility_id: getRemoteUtilityId(),
    consumer_id: consumerId
  });

  const currency = useCurrency()

  const result = agreementData?.result || {};
  const activeAgreement = result?.activeAgreement;
  const agreementHistory = result?.agreementHistory || [];

  const historyColumns = React.useMemo(() => [
  { header: t("Agreement Id"), key: 'agreementNo' as const, render: (val: string) => <span className="font-semibold">{val}</span> },
  { header: t("Start Date"), key: 'startDate' },
  { header: t("End Date"), key: 'endDate' },
  { header: t("Frequency"), key: 'installmentFrequencyDisplay' },
  { header: t("Total Amount"), key: 'totalAgreementAmount' as const, render: (val: string) => <span className="font-semibold">{currency} {val}</span> },
  { 
    header: t("Status"), 
    key: 'statusDisplay' as const,
    type: 'badge',
    badgeConfig: {
      getVariant: (value: string) => {
        if (value === 'Completed') return 'success';
        if (value === 'Cancelled') return 'secondary';
        return 'outline';
      },
      getClassName: (value: string) => {
        if (value === 'Completed') return 'bg-green-50 text-green-600 border-green-200 hover:bg-green-50 font-normal';
        if (value === 'Cancelled') return 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-100 font-normal';
        return '';
      }
    }
  },
  ], [currency, t]);

  return (
    <div className="space-y-8">
      {activeAgreement ? (
        <AgreementDetailsCard agreement={activeAgreement} />
      ) : (
        <Card className="shadow-sm border-gray-200">
          <CardContent className="py-10 text-center text-gray-500">
            {t("No active payment agreement found.")}
          </CardContent>
        </Card>
      )}

      <Card className="shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
          <div className="flex items-center space-x-2">
            <History className="text-gray-400 w-5 h-5" />
            <CardTitle className="text-lg text-gray-800">{t("Agreement History")}</CardTitle>
          </div>
          <div className="text-sm text-muted-foreground">{agreementHistory.length} {t("Records")}</div>
        </CardHeader>
        <CardContent className="pt-4 px-0 pb-0">
          <GenericTable 
            columns={historyColumns} 
            data={agreementHistory}
            actions={[
              {
                label: t("View Details"),
                icon: <Eye className="w-4 h-4 text-gray-500" />,
                onClick: (row: any) => setSelectedHistoryAgreement(row),
              }
            ]}
            actionsType="icons"
          />
        </CardContent>
      </Card>

      <Dialog open={!!selectedHistoryAgreement} onOpenChange={(open) => !open && setSelectedHistoryAgreement(null)}>
        <DialogContent className="max-w-4xl p-0 border-none bg-transparent shadow-none sm:max-w-4xl">
          <DialogTitle className="sr-only">{t("Agreement Details")}</DialogTitle>
          {selectedHistoryAgreement && <AgreementDetailsCard agreement={selectedHistoryAgreement} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Installments;
